// Shapes the document surfaces render (estimate / quote / invoice).
//
// These mirror what GET /orders/:id and GET /o/:uuid actually return. Note the
// decoration shape: `location` is the imprint-location group header ("Centered
// on front") and `charges` are the setup/run rows underneath it. Charges were
// write-only until 2026-07-19 — the schema and POST existed but nothing read
// them back, which is why they never appeared on a document.

export interface DocVariant {
  id: string;
  color: string | null;
  size: string | null;
  material: string | null;
  quantity: number;
  /** ABSENT on client-facing payloads — the public routes strip cost at the
   *  query level (listVariantsForLinePublic). Optional so the same component
   *  renders a rep document and a client document without a second type. */
  unitCost?: string | null;
  /** Per-colour price. NULL means "use the line price" — see the shared money
   *  module. No UI could write this before the document redesign. */
  unitPrice: string | null;
}

export interface DocCharge {
  id: string;
  chargeType: string;
  amount: string;
  /** Rep-facing charge COST (CEO 2026-07-24). ABSENT on client-facing payloads —
   *  the public route strips it via toPublicDecorationCharge, so it never reaches
   *  a client. Feeds the ALL-INCLUSIVE booked-margin display (CEO 2026-07-25),
   *  the same all-in cost basis the commission seed uses. Optional so the one
   *  component renders both a rep and a client document (client payloads omit it).
   *  Always a string when present — the column is NOT NULL DEFAULT '0'. */
  cost?: string;
  billingModel: "fixed" | "per_piece";
  note: string | null;
  /** How many (Stephanie 2026-10-27); null/absent = the default count
   *  (fixed 1, per_piece the line's units). See chargeQuantity. */
  quantity?: number | null;
  /** false = "Subtract from margin": we cover it (sell 0). The client's copy
   *  never receives those rows. */
  displayToClient?: boolean;
}

export interface DocDecoration {
  id: string;
  /** 'item_costs' = the line's "+ Add cost" holder ("Additional costs"), not an
   *  imprint location (Stephanie 2026-10-14). Staff payload only. */
  kind?: "location" | "item_costs";
  /** The imprint location — renders as the italic group header. */
  location: string | null;
  colorCount: number | null;
  /** Presentation→order carry columns (round 34). Returned by GET /orders/:id;
   *  surfaced in the Imprint tab of OrderLineEditModal. Absent on leaner /
   *  client-facing payloads, so all optional — the document card ignores them. */
  imprintType?: string | null;
  proofRequired?: string | null;
  logoName?: string | null;
  logoSize?: string | null;
  logoColor?: string | null;
  repeatLogo?: string | null;
  supplierNotes?: string | null;
  logoFileId?: string | null;
  /** Artwork-as-first-class-PO-input (SO→PO rebuild Round 2). proofType is the
   *  structured proof vocabulary; logoWidthIn/logoHeightIn are inch DIMENSIONS
   *  (numeric → string); useMaxSize defers W/H to the location's max area. */
  proofType?: "none" | "email_proof" | "digital_photo" | "pre_production" | null;
  logoWidthIn?: string | null;
  logoHeightIn?: string | null;
  useMaxSize?: boolean;
  /** The client's copy only (GET /o/:uuid): a signed link to the location's
   *  logo file, so the client can confirm the art (Stephanie 2026-10-14). */
  artwork?: { url: string; name: string; isImage: boolean } | null;
  charges: DocCharge[];
}

export interface DocProductSnapshot {
  name?: string;
  sku?: string;
  supplierName?: string;
  imageUrls?: string[] | Record<string, string>;
}

export interface DocLine {
  id: string;
  lineNumber: number;
  serviceType: string;
  /** THE SKU (catalogue SKU, ATS-10001) — staff payloads only. On a client
   *  payload `productSnapshot.sku` already IS our SKU. */
  catalogueSku?: string | null;
  description: string | null;
  productSnapshot: DocProductSnapshot | null;
  /** ABSENT on client-facing payloads (cost never leaves the server for a
   *  client). Margin is only derivable, and only rendered, when showRepDetail. */
  netCost?: string;
  unitPrice: string;
  quantityTotal: number;
  taxCode?: string | null;
  position?: number;
  /** Titled-section grouping (2026-08-06). NULL/absent = ungrouped, which renders
   *  FIRST with no heading — so every document created before sections existed
   *  looks exactly as it did. Carried from the presentation at convert; see
   *  packages/shared/src/section-carry.ts. */
  sectionId?: string | null;
  variants: DocVariant[];
  /** Client payloads include decorations WITH their charges (that's how setup
   *  and run charges reach the document); may be absent on leaner callers. */
  decorations?: DocDecoration[];
}

/** A titled group on a document. Mirrors the presentation side deliberately, so
 *  @merchd/shared's groupItemsIntoSections works on both without a third
 *  hand-rolled grouping (the second one was a bug that could drop a product).
 *
 *  Carries NOTHING cost-bearing, which is why the client-facing payload needs no
 *  stripping mapper for it. */
export interface DocSection {
  id: string;
  title: string;
  position: number;
}

/**
 * First usable image off a product snapshot. The same "array-or-object, take
 * the first" logic was copy-pasted in four files before this; it lives here now.
 */
export function docLineImageUrl(line: DocLine): string | null {
  const urls = line.productSnapshot?.imageUrls;
  if (!urls) return null;
  if (Array.isArray(urls)) return urls[0] ?? null;
  if (typeof urls === "object") {
    const first = Object.values(urls).find((v) => typeof v === "string" && v !== "");
    return (first as string) ?? null;
  }
  return null;
}

/** "Blue / L / Cotton" → a single readable label for a variant row. */
export function docVariantLabel(v: DocVariant): string {
  const parts = [v.color, v.size, v.material].filter(
    (p): p is string => typeof p === "string" && p.trim() !== "",
  );
  return parts.length > 0 ? parts.join(" / ") : "—";
}

/** The product title, falling back through snapshot → description → line no. */
export function docLineTitle(line: DocLine): string {
  const name = line.productSnapshot?.name?.trim();
  if (name) return name;
  const desc = line.description?.trim();
  if (desc) return desc;
  return `Line ${line.lineNumber}`;
}
